import {AxiosError} from 'axios';

export interface ParsedError {
  message: string;
  cause: string;
  traceId: string;
  statusCode: number;
  resourceId: string;
}

export interface ServiceCommonResponseError {
  message: string;
  traceId: string;
  cause: string;
  resourceId: string;
}

const parseApiError = (error: AxiosError<ServiceCommonResponseError>): ParsedError => {
  const {status, data} = error.response;

  return {
    message: data?.message ?? null,
    cause: data?.cause ?? null,
    traceId: data?.traceId ?? null,
    statusCode: status,
    resourceId: data?.resourceId ?? null,
  };
};

export const parse = (error: AxiosError<ServiceCommonResponseError>): ParsedError => {
  let message = '';
  let cause = null;
  let traceId = null;
  let statusCode = 418;
  let resourceId = null;

  if (error?.response) {
    /**
     * The SDK received an error response from axios.
     *
     * A typical 4xx or 5xx error.
     */
    const parsedError = parseApiError(error);

    message = parsedError.message;
    cause = parsedError.cause;
    traceId = parsedError.traceId;
    resourceId = parsedError.resourceId;

    statusCode = error.response.status || statusCode;
  } else if (error?.request) {
    /**
     * The SDK never received a response, or request never left.
     * The AxiosError object doesn't have a response object, but it contains a request.
     * This happens when the request was successfully made, but a response wasn't received.
     * Potential Scenarios:
     *  - Time out from long running API calls.
     *  - Cross-origin requests and CORS isn't configure properly.
     *  - Valid cross-origin request, but the API throws an error.
     *  - Bad network connection.
     *  - Network Error
     */
    message = `Response not set, but request is set. - ${error.message}`;
  } else {
    /**
     * Default message - If the two scenario above don't occur.
     *
     * If the error doesn't contain a response or request property then
     * Axios failed
     */
    message = `Axios likely failed. - ${error?.message}`;
  }

  return {
    message,
    cause,
    traceId,
    statusCode,
    resourceId,
  };
};
